import React from "react";
import {
  FormControl,
  Select,
  MenuItem,
  Typography,
  Box,
  SelectChangeEvent,
  TextField,
  Autocomplete,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { CONTROL_HEIGHT, CONTROL_RADIUS, NJIM } from "../../../../theme/njimTheme";

interface SelectFieldProps {
  name: string;
  value: string | number;
  onChange: (event: SelectChangeEvent<string | number>) => void;
  options: { label: string; value: number | string }[];
  label?: string;
  placeHolder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  readOnly?: boolean;
  Textcolors?: string;
  resettable?: boolean;
  bgColor?: string;
  minWidth?: string;
  searchable?: boolean;
}

const CustomSelect: React.FC<SelectFieldProps> = ({
  name,
  value,
  onChange,
  options,
  label,
  placeHolder = "Select",
  required,
  disabled,
  error,
  readOnly,
  Textcolors,
  bgColor,
  resettable = false,
  searchable,
  minWidth,
}) => {
  if (searchable) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          marginBottom: error ? "0px" : "8px",
        }}
      >
        {label && (
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
              fontSize: "14px",
              display: "flex",
              color: NJIM.textPrimary,
            }}
          >
            {label}
            {required && (
              <span style={{ color: NJIM.danger, marginInlineStart: "5px" }}>*</span>
            )}
          </Typography>
        )}

        <Autocomplete
          options={options}
          getOptionLabel={(option) => option.label}
          value={options.find((opt) => opt.value === value) || null}
          popupIcon={<ExpandMoreIcon />}
          forcePopupIcon
          disabled={disabled}
          onChange={(_, newValue) =>
            onChange({
              target: {
                name,
                value: newValue ? newValue.value : "",
              },
            } as SelectChangeEvent<string | number>)
          }
          filterOptions={(opts, state) =>
            opts.filter((opt) =>
              opt.label.toLowerCase().includes(state.inputValue.toLowerCase())
            )
          }
          sx={{
            "& .MuiAutocomplete-clearIndicator": {
              color: NJIM.ink,
            },
          }}
          renderInput={(params) => (
            <TextField
              sx={{
                "& .MuiOutlinedInput-root": {
                  padding: "8px",
                  height: CONTROL_HEIGHT,
                  fontSize: "14px",

                  color: NJIM.textSecondary,
                  fontWeight: "normal",
                  border: "none",
                },
                "& .MuiInputBase-input": {
                  color: NJIM.textSecondary,
                  zIndex: 10,
                  "&::placeholder": {
                    color: NJIM.textSecondary,
                    opacity: 1,
                    zIndex: 10,
                  },
                  overflow: "auto",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: NJIM.textSecondary,
                  opacity: 1,
                },
                "& .MuiSvgIcon-root": {
                  color: NJIM.ink,
                  zIndex: 10,
                },

                "& .Mui-error": {
                  color: NJIM.danger,
                },
                "& fieldset": {
                  border: "none",

                  borderRadius: "8px",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: `${NJIM.textSecondary} !important`,
                },
                "&:hover fieldset, &.Mui-focused fieldset": {
                  borderRadius: "5px",
                  border: `2px solid ${NJIM.mint} !important`,
                },
              }}
              {...params}
              placeholder={placeHolder}
              error={!!error}
            />
          )}
        />

        {error && (
          <Typography
            className="f-12"
            sx={{
              fontWeight: "medium",
              color: NJIM.danger,
            }}
          >
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        marginBottom: error ? "0px" : "8px",
      }}
    >
      {label && (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "bold",
            fontSize: "14px",
            display: "flex",
            color: NJIM.textPrimary,
          }}
        >
          {label}{" "}
          {required && (
            <span
              style={{ color: NJIM.danger, marginInlineStart: "5px", lineHeight: "1.5" }}
            >
              *
            </span>
          )}
        </Typography>
      )}
      <FormControl fullWidth variant="outlined" error={!!error}>
        <Select
          name={name}
          value={value}
          onChange={onChange}
          displayEmpty
          disabled={disabled}
          readOnly={readOnly}
          IconComponent={readOnly ? () => null : ExpandMoreIcon}
          sx={{
            borderRadius: "0px",
            height: CONTROL_HEIGHT,
            fontSize: "14px",
            minWidth: minWidth,
            padding: "8px",
            "& .MuiSelect-select.Mui-disabled": {
              color: NJIM.ink,
            },
            "& .MuiOutlinedInput-root.Mui-disabled": {
              "& fieldset": {
                borderColor: `${NJIM.textSecondary} !important`,
              },
            },

            "& .MuiOutlinedInput-input.Mui-disabled": {
              color: NJIM.ink,

              WebkitTextFillColor: "unset !important",
            },
            "&::placeholder": {
              color: NJIM.ink,
              opacity: 1,
              zIndex: 10,
            },
            "& input::placeholder": {
              color: NJIM.ink,

              opacity: 1,
            },
            "&:hover fieldset": {
              borderColor: `${NJIM.textMuted} !important`,
            },
            "& .MuiSelect-select": {
              padding: "10px",
              color: Textcolors || NJIM.textSecondary,
              zIndex: 10,
            },

            "& .MuiSvgIcon-root": {
              color: NJIM.ink,
              zIndex: 10,
            },

            "& fieldset": {
              border: "none",
              background: bgColor || NJIM.white,
              borderRadius: CONTROL_RADIUS,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: `${NJIM.textSecondary} !important`,
            },
            "&:hover fieldset, &.Mui-focused fieldset": {
              borderRadius: "5px",
              border: `2px solid ${NJIM.mint} !important`,
            },
          }}
        >
          {resettable ? (
            <MenuItem sx={{ color: NJIM.textMuted }} value="">
              {placeHolder}
            </MenuItem>
          ) : (
            <MenuItem value="" disabled>
              {placeHolder}
            </MenuItem>
          )}
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {error && (
        <Typography
          className="f-12"
          sx={{
            fontWeight: "medium",
            textAlign: "start",
            color: NJIM.danger,
            paddingTop: "0px",
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default CustomSelect;
